import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import {
  DollarSign,
  Ruler,
  Home,
  TrendingUp,
  Users,
  Tag,
  Building2,
} from "lucide-react";
import {
  formatCurrency,
  formatSquareFeet,
} from "../../../utils/property-formatters";
import { useMemo } from "react";
import { getLatestListingFromListings } from "../../../utils/property-calculations";

export const PropertyOverview = ({
  property,
  squareFeet,
  estimatedValue,
  estimatedRentAmount,
}) => {
  const latestListing = useMemo(
    () => getLatestListingFromListings(property),
    [property]
  );

  const {
    loanBalance = 0,
    onMarket = false,
    yearBuilt,
    hoa,
    hoa1Name,
    hoa1Amount,
    hoa1Frequency,
    constructionType,
    roofType,
    roofCoverType,
    stories,
  } = property;

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="shadow-md border-l-4 border-l-primary">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground mb-1">
                  Estimated Value
                </p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-primary">
                  {formatCurrency(estimatedValue)}
                </p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground mb-1">
                  Monthly Rent
                </p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-green-600">
                  {formatCurrency(estimatedRentAmount)}
                </p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-green-100 flex items-center justify-center">
                <Home className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-md border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground mb-1">
                  Property Size
                </p>
                <p className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600">
                  {formatSquareFeet(squareFeet)}
                </p>
              </div>
              <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Ruler className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Information Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Financial Details */}
        <Card className="shadow-md hover:shadow-lg transition-shadow bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              Financial Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
              <span className="text-xs sm:text-sm text-muted-foreground">
                Open Loan Balance
              </span>
              <span className="text-sm sm:text-base font-bold">
                {formatCurrency(loanBalance)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-primary/5 rounded-lg">
              <span className="text-xs sm:text-sm text-muted-foreground">
                Estimated Value
              </span>
              <span className="text-sm sm:text-base font-bold text-primary">
                {formatCurrency(estimatedValue)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
              <span className="text-xs sm:text-sm text-muted-foreground">
                Est. Monthly Rent
              </span>
              <span className="text-sm sm:text-base font-bold text-green-600">
                {formatCurrency(estimatedRentAmount)}/mo
              </span>
            </div>
          </CardContent>
        </Card>

        {/* HOA Information */}
        {hoa && hoa1Name && (
          <Card className="shadow-md hover:shadow-lg transition-shadow bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                HOA Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-muted/30 rounded-lg">
                <span className="text-xs sm:text-sm text-muted-foreground block mb-1">
                  Association Name
                </span>
                <span className="text-sm sm:text-base font-semibold">
                  {hoa1Name}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                <span className="text-xs sm:text-sm text-muted-foreground">
                  HOA Fee ({hoa1Frequency})
                </span>
                <span className="text-sm sm:text-base font-bold text-orange-700">
                  {formatCurrency(hoa1Amount)}
                </span>
              </div>
              {hoa1Frequency &&
                hoa1Frequency.toLowerCase() === "annual" &&
                hoa1Amount && (
                  <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                    <span className="text-xs sm:text-sm text-muted-foreground">
                      Monthly Equivalent
                    </span>
                    <span className="text-sm sm:text-base font-bold">
                      {formatCurrency(hoa1Amount / 12)}/mo
                    </span>
                  </div>
                )}
            </CardContent>
          </Card>
        )}

        {/* Listing Information */}
        {onMarket && latestListing && (
          <Card className="shadow-md hover:shadow-lg transition-shadow bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Tag className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                Listing Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-xs sm:text-sm text-muted-foreground">
                  Listing Price
                </span>
                <span className="text-sm sm:text-base font-bold text-green-700">
                  {formatCurrency(
                    latestListing.amount ??
                      latestListing.listPrice ??
                      latestListing.price
                  )}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-xs sm:text-sm text-muted-foreground">
                  Days on Market
                </span>
                <span className="text-sm sm:text-base font-bold">
                  {latestListing.daysOnMarket ?? "N/A"} days
                </span>
              </div>
              {latestListing.mlsNumber && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <span className="text-xs sm:text-sm text-muted-foreground block mb-1">
                    MLS Number
                  </span>
                  <span className="text-sm sm:text-base font-semibold">
                    {latestListing.mlsNumber}
                  </span>
                </div>
              )}
              {latestListing.status && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <span className="text-xs sm:text-sm text-muted-foreground block mb-1">
                    Status
                  </span>
                  <Badge
                    variant="secondary"
                    className="bg-blue-100 text-blue-700 text-xs sm:text-sm"
                  >
                    {latestListing.status}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Construction Details */}
        {(constructionType || roofType || stories) && (
          <Card className="shadow-md hover:shadow-lg transition-shadow bg-white">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                Construction Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {constructionType && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <span className="text-xs sm:text-sm text-muted-foreground block mb-1">
                    Construction Type
                  </span>
                  <span className="text-sm sm:text-base font-semibold">
                    {constructionType}
                  </span>
                </div>
              )}
              {roofType && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <span className="text-xs sm:text-sm text-muted-foreground block mb-1">
                    Roof Type
                  </span>
                  <span className="text-sm sm:text-base font-semibold">
                    {roofType}
                    {roofCoverType && ` - ${roofCoverType}`}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {stories && (
                  <div className="p-3 bg-muted/30 rounded-lg text-center">
                    <span className="text-xs sm:text-sm text-muted-foreground block mb-1">
                      Stories
                    </span>
                    <span className="text-base sm:text-lg font-bold">
                      {stories}
                    </span>
                  </div>
                )}
                {yearBuilt && (
                  <div className="p-3 bg-muted/30 rounded-lg text-center">
                    <span className="text-xs sm:text-sm text-muted-foreground block mb-1">
                      Year Built
                    </span>
                    <span className="text-base sm:text-lg font-bold">
                      {yearBuilt}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
